import axios from 'axios';
import { api } from './config/axios';
import { store } from '../store';

export const getLiveTransactions = (params = {}) => api.get('/api/v1/admin/transactions/live', params);

export const getLiveTransactionDetail = (idOrRef) =>
  api.get(`/api/v1/admin/transactions/live/${encodeURIComponent(idOrRef)}`);

function getMainAuthApiUrl() {
  return process.env.NODE_ENV === 'development'
    ? 'http://localhost:8000'
    : process.env.REACT_APP_API_URL || 'http://localhost:8000';
}

export function resolveTenantStreamRequestContext() {
  let baseURL =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:8080'
      : process.env.REACT_APP_API_URL || 'http://localhost:8000';

  let token = store.getState().auth?.token;
  if (!token) {
    try {
      const userSession = localStorage.getItem('user_session');
      if (userSession) {
        token = JSON.parse(userSession)?.token;
      }
    } catch {
      // ignore storage parse error
    }
  }

  let appId = null;
  try {
    const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
    if (bootstrappedApp) {
      const app = JSON.parse(bootstrappedApp);
      if (process.env.NODE_ENV !== 'development' && app?.urls?.apiUrl) {
        baseURL = app.urls.apiUrl;
      }
      appId = app?._id || null;
    } else {
      const activeApp = localStorage.getItem('activeApp');
      if (activeApp) {
        const app = JSON.parse(activeApp);
        if (process.env.NODE_ENV !== 'development' && app?.urls?.apiUrl) {
          baseURL = app.urls.apiUrl;
        }
        appId = app?._id || null;
      }
    }
  } catch {
    // ignore storage parse error
  }

  return {
    baseURL: String(baseURL || '').replace(/\/+$/, ''),
    token: token || null,
    appId,
  };
}

async function tryRefreshAdminToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return false;

  try {
    const res = await axios.post(`${getMainAuthApiUrl()}/api/v1/user/refresh`, {
      refreshToken,
    });
    if (res.data?.success && res.data?.data?.authorization?.token) {
      const newToken = res.data.data.authorization.token;
      const newRefreshToken = res.data.data.refreshToken;
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }
      const userSession = localStorage.getItem('user_session')
        ? JSON.parse(localStorage.getItem('user_session'))
        : {};
      userSession.token = newToken;
      localStorage.setItem('user_session', JSON.stringify(userSession));
      store.dispatch({
        type: 'LOGIN_SUCCEED',
        payload: userSession,
      });
      return true;
    }
  } catch {
    // fallback handled by caller
  }
  return false;
}

function parseSseBlock(rawBlock) {
  const lines = rawBlock.split(/\r?\n/);
  let id = null;
  let event = 'message';
  const dataLines = [];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line || line.startsWith(':')) {
      // Comment / keep-alive line
    } else if (line.startsWith('id:')) {
      id = line.slice(3).trim();
    } else if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (dataLines.length === 0 && event === 'message') {
    return null;
  }

  const rawData = dataLines.join('\n');
  let parsedData = rawData;
  if (rawData) {
    try {
      parsedData = JSON.parse(rawData);
    } catch {
      parsedData = rawData;
    }
  }

  return { id, event, data: parsedData };
}

/**
 * Connects an authenticated SSE stream using fetch + ReadableStream so JWT and x-app-id
 * remain strictly in request headers (never leaked in URLs), with 25s stall detection,
 * Last-Event-ID replay, and automatic 3s cursor-polling fallback if streaming is blocked.
 */
export function connectLiveTransactionsStream({
  getLastEventId,
  onConnected,
  onEvent,
  onResyncRequired,
  onModeChange,
  onFallbackPoll,
}) {
  let disposed = false;
  let activeAbortController = null;
  let reconnectTimer = null;
  let pollInterval = null;
  let stallTimer = null;
  let consecutiveFailures = 0;

  const clearTimers = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (stallTimer) {
      clearTimeout(stallTimer);
      stallTimer = null;
    }
  };

  const stopPollingFallback = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  const startPollingFallback = () => {
    if (disposed || pollInterval) return;
    if (onModeChange) onModeChange('polling');
    if (onFallbackPoll) {
      Promise.resolve(onFallbackPoll()).catch(() => {});
    }
    pollInterval = setInterval(() => {
      if (!disposed && onFallbackPoll) {
        Promise.resolve(onFallbackPoll()).catch(() => {});
      }
    }, 3000);
  };

  const resetStallWatchdog = () => {
    if (stallTimer) clearTimeout(stallTimer);
    // Server sends heartbeat every 10s; >25s of silence indicates half-open socket
    stallTimer = setTimeout(() => {
      if (activeAbortController) {
        activeAbortController.abort();
      }
    }, 25000);
  };

  const scheduleReconnect = (delayMs) => {
    if (disposed) return;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      openStream();
    }, delayMs);
  };

  async function openStream(retriedAfterRefresh = false) {
    if (disposed) return;
    clearTimers();

    if (!pollInterval && onModeChange) {
      onModeChange(consecutiveFailures > 0 ? 'reconnecting' : 'connecting');
    }

    const { baseURL, token, appId } = resolveTenantStreamRequestContext();
    if (!baseURL || !token) {
      startPollingFallback();
      scheduleReconnect(10000);
      return;
    }

    const headers = {
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
      Authorization: `Bearer ${token}`,
    };
    if (appId) {
      headers['x-app-id'] = appId;
    }
    const lastId = typeof getLastEventId === 'function' ? getLastEventId() : null;
    if (lastId) {
      headers['Last-Event-ID'] = String(lastId);
    }

    const controller = new AbortController();
    activeAbortController = controller;

    try {
      resetStallWatchdog();
      const response = await fetch(`${baseURL}/api/v1/admin/transactions/live/stream`, {
        method: 'GET',
        headers,
        signal: controller.signal,
        cache: 'no-store',
      });

      if (disposed) return;

      if (response.status === 401 && !retriedAfterRefresh) {
        const refreshed = await tryRefreshAdminToken();
        if (refreshed && !disposed) {
          openStream(true);
          return;
        }
      }

      if (!response.ok || !response.body) {
        throw new Error(`SSE stream returned HTTP ${response.status}`);
      }

      // Stream established — stop fallback polling if it was active
      consecutiveFailures = 0;
      stopPollingFallback();
      if (onModeChange) onModeChange('streaming');
      resetStallWatchdog();

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (done || disposed) break;

        resetStallWatchdog();
        buffer += decoder.decode(value, { stream: true });

        let boundaryMatch = buffer.match(/\r?\n\r?\n/);
        while (boundaryMatch) {
          const splitIndex = boundaryMatch.index;
          const rawBlock = buffer.slice(0, splitIndex);
          buffer = buffer.slice(splitIndex + boundaryMatch[0].length);

          const frame = parseSseBlock(rawBlock);
          if (frame) {
            if (frame.event === 'connected') {
              if (onConnected) onConnected(frame.data);
            } else if (frame.event === 'resync_required') {
              if (onResyncRequired) onResyncRequired(frame.data);
            } else if (frame.event !== 'heartbeat') {
              if (onEvent) onEvent(frame);
            }
          }

          boundaryMatch = buffer.match(/\r?\n\r?\n/);
        }
      }

      if (!disposed) {
        consecutiveFailures += 1;
        scheduleReconnect(1500);
      }
    } catch {
      if (disposed) return;
      consecutiveFailures += 1;

      if (consecutiveFailures >= 2) {
        startPollingFallback();
        scheduleReconnect(20000);
      } else {
        const backoff = Math.min(1000 * 2 ** (consecutiveFailures - 1), 8000);
        scheduleReconnect(backoff);
      }
    }
  }

  openStream();

  return {
    disconnect: () => {
      disposed = true;
      clearTimers();
      stopPollingFallback();
      if (activeAbortController) {
        activeAbortController.abort();
        activeAbortController = null;
      }
    },
  };
}

